(() => {
  'use strict';

  const ROOT_SELECTOR = '.cgau-gradebook';
  const COMPONENT_SELECTOR = '[data-cgau-condition-parcel]';
  const GRADES = {
    brand: {
      label: 'Brand New',
      short: 'Factory-fresh presentation',
      condition: 'New and unused.',
      packaging: 'Always factory-sealed in original retail packaging.',
      parcelLabel: 'FACTORY-SEALED RETAIL CARTON',
    },
    open: {
      label: 'New Open-box',
      short: 'New unit, opened packaging',
      condition: 'New and unused. It is not refurbished or renewed.',
      packaging: 'The retail box has been opened or the item has been repacked.',
      parcelLabel: 'OPEN-BOX • NEW UNIT',
    },
    damaged: {
      label: 'New (Box Damaged)',
      short: 'The mark belongs to the carton',
      condition: 'New and unused.',
      packaging: 'The outer box may be marked, dented, torn, crushed or creased. Any item damage must be separately disclosed.',
      parcelLabel: 'CARTON MARKED • UNIT NEW',
    },
    nobox: {
      label: 'New No Box',
      short: 'Retail carton removed from the picture',
      condition: 'New and unused, commonly separated from a larger multi-device kit.',
      packaging: 'No individual retail box. Protective or OEM packaging is used for dispatch as described.',
      parcelLabel: 'NO INDIVIDUAL RETAIL BOX',
    },
    renewed: {
      label: 'Manufacturer Renewed',
      short: 'Refurbished through the manufacturer program',
      condition: 'Professionally refurbished and tested through Anker’s official program. It may have been previously used.',
      packaging: 'Retail packaging is not assumed. The listing describes the supplied packaging where material.',
      parcelLabel: 'MANUFACTURER RENEWED',
    },
  };

  const SUBGRADES = {
    excellent: {
      label: 'Excellent',
      copy: 'Near-new appearance. Any marks should be faint and hard to notice, such as fine surface marks from light handling. The casing should look clean and well cared for. Professionally refurbished and tested; check the individual listing for any specific marks.',
    },
    good: {
      label: 'Good',
      copy: 'Clean overall, with light scratches, rub marks or small scuffs that become visible on close inspection. Expect some cosmetic signs of previous handling or use rather than a near-new finish. Professionally refurbished and tested; the listing describes any item-specific wear.',
    },
    fair: {
      label: 'Fair',
      copy: 'More noticeable cosmetic wear, such as visible scratches, scuffing or worn areas on the outer casing. Expect an obviously used appearance. The grade describes appearance: the unit is still professionally refurbished and tested. Check the listing and photos for the particular marks.',
    },
  };

  const componentMarkup = (instance) => `
    <section class="cgau-condition-parcel" data-cgau-condition-parcel data-grade="brand" data-subgrade="excellent" data-motion="paused" aria-labelledby="cgau-parcel-title-${instance}">
      <div class="cgau-condition-parcel__intro">
        <div>
          <span class="cgau-condition-parcel__eyebrow">Parcel decoder</span>
          <h2 id="cgau-parcel-title-${instance}">Change the grade. See what changes.</h2>
        </div>
        <p>This illustrated parcel explains condition and packaging. It is not an exact product, accessory list or promise of what is inside a particular order.</p>
      </div>

      <div class="cgau-condition-parcel__selector" role="radiogroup" aria-label="Choose a condition grade">
        <span class="cgau-condition-parcel__selector-light" aria-hidden="true"></span>
        <div class="cgau-condition-parcel__indicator" aria-hidden="true"></div>
        ${Object.entries(GRADES).map(([key, grade], index) => `
          <button class="cgau-condition-parcel__grade" type="button" role="radio" data-cgau-grade="${key}" aria-checked="${index === 0 ? 'true' : 'false'}" tabindex="${index === 0 ? '0' : '-1'}">
            <span class="cgau-condition-parcel__grade-number">0${index + 1}</span>
            <span>${grade.label}</span>
          </button>
        `).join('')}
      </div>

      <div class="cgau-condition-parcel__workbench">
        <div class="cgau-condition-parcel__scene" aria-hidden="true">
          <div class="cgau-condition-parcel__bench-line"></div>
          <div class="cgau-condition-parcel__parcel-shadow"></div>
          <div class="cgau-condition-parcel__parcel">
            <svg class="cgau-condition-parcel__drawing" viewBox="0 0 520 360" role="presentation" focusable="false">
              <defs>
                <linearGradient id="cgau-cp-body-${instance}" x1="0" y1="0" x2="1" y2="1"><stop stop-color="#fff"/><stop offset=".6" stop-color="#eef3f6"/><stop offset="1" stop-color="#b8c8d4"/></linearGradient>
                <radialGradient id="cgau-cp-glass-${instance}" cx=".3" cy=".25" r=".75"><stop stop-color="#619cbb"/><stop offset=".32" stop-color="#233f57"/><stop offset="1" stop-color="#091925"/></radialGradient>
                <linearGradient id="cgau-cp-top-${instance}" x1="0" y1="0" x2="1" y2="1"><stop stop-color="#f5f0e5"/><stop offset="1" stop-color="#d9d4ca"/></linearGradient>
                <linearGradient id="cgau-cp-front-${instance}" x1="0" y1="0" x2=".9" y2="1"><stop stop-color="#ece8de"/><stop offset="1" stop-color="#c8ccc5"/></linearGradient>
                <linearGradient id="cgau-cp-card-${instance}" x1="0" y1="0" x2="1" y2="1"><stop stop-color="#dca667"/><stop offset="1" stop-color="#aa6f39"/></linearGradient>
                <linearGradient id="cgau-cp-wrap-${instance}" x1="0" y1="0" x2="1" y2="1"><stop stop-color="#e6fff9" stop-opacity=".82"/><stop offset="1" stop-color="#8bd2c3" stop-opacity=".38"/></linearGradient>
                <filter id="cgau-cp-shadow-${instance}" x="-30%" y="-30%" width="160%" height="180%"><feDropShadow dx="0" dy="12" stdDeviation="10" flood-opacity=".3"/></filter>
              </defs>
              <ellipse cx="260" cy="318" rx="166" ry="12" fill="#426779" opacity=".16"/>

              <g class="cgau-cp-state cgau-cp-state--closed" filter="url(#cgau-cp-shadow-${instance})">
                <polygon class="cgau-cp-face cgau-cp-face--side" points="94,119 205,171 205,309 94,257"/>
                <polygon class="cgau-cp-face cgau-cp-face--front" points="205,171 426,124 426,262 205,309"/>
                <polygon class="cgau-cp-face cgau-cp-face--top" points="94,119 315,72 426,124 205,171"/>
                <polygon class="cgau-cp-tape" points="201,96 231,90 342,142 312,149"/>
                <polygon class="cgau-cp-tape cgau-cp-tape--front" points="312,149 342,142 342,280 312,286"/>
                <line class="cgau-cp-join" x1="94" y1="119" x2="205" y2="171"/>
                <g class="cgau-cp-seal-badge" transform="translate(323 204) skewY(-12)"><rect x="-62" y="-18" width="124" height="36" rx="4"/><text text-anchor="middle" y="-2" data-cgau-carton-badge-one>FACTORY</text><text text-anchor="middle" y="11" data-cgau-carton-badge-two>SEALED</text></g>
                <g class="cgau-cp-carton-camera" transform="matrix(1 .468 0 1 120 174) scale(.85)"><rect width="54" height="42" rx="10"/><circle cx="27" cy="21" r="12"/><circle cx="27" cy="21" r="5"/></g>
                <text class="cgau-cp-face-label" text-anchor="middle" transform="translate(318 260) skewY(-12)">RETAIL CARTON</text>
                <g class="cgau-cp-damage"><path d="M403 159l-24 18 17 16-25 20 20 22"/><path d="M414 232l-17 8 12 10-18 9"/></g>
              </g>

              <g class="cgau-cp-state cgau-cp-state--open" filter="url(#cgau-cp-shadow-${instance})">
                <polygon class="cgau-cp-open-flap cgau-cp-open-flap--back" points="104,178 300,136 288,76 92,118"/>
                <polygon class="cgau-cp-open-flap cgau-cp-open-flap--right" points="300,136 404,181 466,139 359,94"/>
                <polygon class="cgau-cp-open-flap cgau-cp-open-flap--left" points="104,178 205,223 151,270 50,224"/>
                <polygon class="cgau-cp-open-well" points="104,178 300,136 404,181 205,223"/>
                <g class="cgau-cp-device cgau-cp-device--inside" transform="translate(213 126)"><path d="M18 81v19M5 101h26"/><rect style="fill:url(#cgau-cp-body-${instance})" x="-22" y="0" width="80" height="82" rx="22"/><circle style="fill:url(#cgau-cp-glass-${instance})" cx="18" cy="37" r="23"/><circle cx="18" cy="37" r="11"/><circle cx="39" cy="17" r="2.5"/><ellipse class="cgau-cp-glass-highlight" cx="10" cy="28" rx="5" ry="3" transform="rotate(-35 10 28)"/></g>
                <polygon class="cgau-cp-card-side" points="104,178 205,223 205,325 104,280"/>
                <polygon class="cgau-cp-card-front" points="205,223 404,181 404,283 205,325"/>
                <text class="cgau-cp-card-label" text-anchor="middle" transform="translate(306 270) skewY(-12)">OPEN BOX · NEW UNIT</text>
                <path class="cgau-cp-card-seam" d="M205 223l199-42M205 223L104 178"/>
              </g>

              <g class="cgau-cp-state cgau-cp-state--nobox">
                <g class="cgau-cp-device cgau-cp-device--free" transform="translate(220 113)"><path d="M18 99v28M-2 128h40"/><rect style="fill:url(#cgau-cp-body-${instance})" x="-35" y="0" width="106" height="101" rx="29"/><circle style="fill:url(#cgau-cp-glass-${instance})" cx="18" cy="44" r="31"/><circle cx="18" cy="44" r="14"/><circle cx="48" cy="20" r="3"/><ellipse class="cgau-cp-glass-highlight" cx="7" cy="32" rx="7" ry="4" transform="rotate(-35 7 32)"/></g>
                <rect class="cgau-cp-wrap-shell" x="153" y="91" width="215" height="204" rx="42" fill="url(#cgau-cp-wrap-${instance})"/>
                <g class="cgau-cp-bubbles"><circle cx="180" cy="122" r="10"/><circle cx="342" cy="126" r="11"/><circle cx="176" cy="190" r="12"/><circle cx="341" cy="207" r="9"/><circle cx="199" cy="267" r="11"/><circle cx="319" cy="267" r="12"/></g>
                <path class="cgau-cp-wrap-fold" d="M166 153l33-43M353 160l-31-48M168 238l34 43M352 241l-34 39"/>
                <g class="cgau-cp-wrap-label"><rect x="176" y="278" width="168" height="34" rx="5"/><text x="260" y="300" text-anchor="middle">PROTECTIVE PACKAGING</text></g>
              </g>

              <g class="cgau-cp-state cgau-cp-state--renewed" filter="url(#cgau-cp-shadow-${instance})">
                <g class="cgau-cp-plain-box">
                  <polygon points="78,151 244,116 326,154 160,190" class="cgau-cp-plain-top"/>
                  <polygon points="78,151 160,190 160,295 78,256" class="cgau-cp-plain-side"/>
                  <polygon points="160,190 326,154 326,259 160,295" class="cgau-cp-plain-front"/>
                  <path d="M202 125l82 38v105" class="cgau-cp-plain-tape"/>
                  <text text-anchor="middle" transform="translate(244 258) skewY(-12)" class="cgau-cp-plain-label">PACKAGING VARIES</text>
                </g>
                <g class="cgau-cp-device cgau-cp-device--renewed" transform="translate(363 165)"><path d="M18 99v28M-2 128h40"/><rect style="fill:url(#cgau-cp-body-${instance})" x="-35" y="0" width="106" height="101" rx="29"/><circle style="fill:url(#cgau-cp-glass-${instance})" cx="18" cy="44" r="31"/><circle cx="18" cy="44" r="14"/><circle cx="48" cy="20" r="3"/><ellipse class="cgau-cp-glass-highlight" cx="7" cy="32" rx="7" ry="4" transform="rotate(-35 7 32)"/><path class="cgau-cp-cosmetic cgau-cp-cosmetic--one" d="M-19 20l10-3m-9 8 13-4"/><path class="cgau-cp-cosmetic cgau-cp-cosmetic--two" d="M43 78l15-5m-11 11 13-4M-20 73l10 4m-10 5 13 4"/></g>
                <g class="cgau-cp-renewed-label" transform="rotate(-4 260 316)"><rect x="171" y="295" width="178" height="40" rx="5"/><text x="260" y="313" text-anchor="middle">MANUFACTURER</text><text x="260" y="327" text-anchor="middle">RENEWED</text></g>
              </g>
            </svg>
            <span data-cgau-carton-label hidden>RETAIL CARTON</span>
          </div>
          <div class="cgau-condition-parcel__caption">
            <span class="cgau-condition-parcel__caption-pin"></span>
            Illustrated condition guide · exact parcel varies
          </div>
        </div>

        <aside class="cgau-condition-parcel__docket" aria-labelledby="cgau-parcel-state-${instance}">
          <div class="cgau-condition-parcel__docket-clip" aria-hidden="true"></div>
          <h3 id="cgau-parcel-state-${instance}" data-cgau-parcel-title>${GRADES.brand.label}</h3>
          <p class="cgau-condition-parcel__docket-short" data-cgau-parcel-short>${GRADES.brand.short}</p>
          <dl>
            <div><dt>Unit</dt><dd data-cgau-parcel-condition>${GRADES.brand.condition}</dd></div>
            <div><dt>Packaging</dt><dd data-cgau-parcel-packaging>${GRADES.brand.packaging}</dd></div>
          </dl>
          <div class="cgau-condition-parcel__renewed-controls" data-cgau-renewed-controls hidden>
            <span>Cosmetic grade shown</span>
            <div class="cgau-condition-parcel__subgrades" role="radiogroup" aria-label="Choose a Manufacturer Renewed cosmetic grade">
              ${Object.entries(SUBGRADES).map(([key, subgrade], index) => `<button type="button" role="radio" data-cgau-subgrade="${key}" aria-checked="${index === 0 ? 'true' : 'false'}" tabindex="${index === 0 ? '0' : '-1'}">${subgrade.label}</button>`).join('')}
            </div>
            <p data-cgau-subgrade-copy>${SUBGRADES.excellent.copy}</p>
          </div>
          <p class="cgau-condition-parcel__contents-note"><strong>Contents stay listing-specific.</strong> This illustration does not add, remove or promise accessories. Read the exact included-contents wording for the product you are buying.</p>
          <span class="cgau-condition-parcel__stamp" data-cgau-parcel-label>${GRADES.brand.parcelLabel}</span>
        </aside>
      </div>

      <p class="cgau-condition-parcel__status" role="status" aria-live="polite" aria-atomic="true" data-cgau-parcel-status>Brand New selected. New and unused. Always factory-sealed in original retail packaging.</p>
    </section>
  `;

  const setup = (root, instance) => {
    if (root.querySelector(COMPONENT_SELECTOR)) return;
    const firstGradeSection = root.querySelector('.cgau-gradebook__section');
    if (!firstGradeSection) return;

    const holder = document.createElement('div');
    holder.innerHTML = componentMarkup(instance).trim();
    const component = holder.firstElementChild;
    firstGradeSection.before(component);

    const gradeButtons = [...component.querySelectorAll('[data-cgau-grade]')];
    const subgradeButtons = [...component.querySelectorAll('[data-cgau-subgrade]')];
    const selector = component.querySelector('.cgau-condition-parcel__selector');
    const title = component.querySelector('[data-cgau-parcel-title]');
    const short = component.querySelector('[data-cgau-parcel-short]');
    const condition = component.querySelector('[data-cgau-parcel-condition]');
    const packaging = component.querySelector('[data-cgau-parcel-packaging]');
    const parcelLabel = component.querySelector('[data-cgau-parcel-label]');
    const renewedControls = component.querySelector('[data-cgau-renewed-controls]');
    const subgradeCopy = component.querySelector('[data-cgau-subgrade-copy]');
    const cartonLabel = component.querySelector('[data-cgau-carton-label]');
    const cartonBadgeOne = component.querySelector('[data-cgau-carton-badge-one]');
    const cartonBadgeTwo = component.querySelector('[data-cgau-carton-badge-two]');
    const status = component.querySelector('[data-cgau-parcel-status]');
    const reduceMotion = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)');
    let activeGrade = 'brand';
    let activeSubgrade = 'excellent';
    let inView = false;
    let animationTimer = 0;
    let animationFrame = 0;

    const motionAllowed = () => !reduceMotion?.matches && !document.hidden && inView;

    const updateIndicator = () => {
      const activeButton = gradeButtons.find((button) => button.dataset.cgauGrade === activeGrade);
      if (!activeButton || !selector) return;
      selector.style.setProperty('--cgau-parcel-indicator-x', `${activeButton.offsetLeft}px`);
      selector.style.setProperty('--cgau-parcel-indicator-y', `${activeButton.offsetTop}px`);
      selector.style.setProperty('--cgau-parcel-indicator-width', `${activeButton.offsetWidth}px`);
      selector.style.setProperty('--cgau-parcel-indicator-height', `${activeButton.offsetHeight}px`);
    };

    const stopAnimation = () => {
      globalThis.clearTimeout(animationTimer);
      globalThis.cancelAnimationFrame?.(animationFrame);
      animationTimer = 0;
      animationFrame = 0;
      component.classList.remove('is-animating');
    };

    const restartFiniteAnimation = () => {
      stopAnimation();
      if (!motionAllowed()) return;
      animationFrame = requestAnimationFrame(() => {
        animationFrame = 0;
        if (!motionAllowed()) return;
        component.classList.add('is-animating');
        animationTimer = globalThis.setTimeout(() => component.classList.remove('is-animating'), 1100);
      });
    };

    const setSubgrade = (key, announce = true) => {
      const subgrade = SUBGRADES[key];
      if (!subgrade) return;
      activeSubgrade = key;
      component.dataset.subgrade = key;
      subgradeButtons.forEach((button) => {
        const selected = button.dataset.cgauSubgrade === key;
        button.setAttribute('aria-checked', String(selected));
        button.tabIndex = selected ? 0 : -1;
      });
      subgradeCopy.textContent = subgrade.copy;
      if (announce) status.textContent = `Manufacturer Renewed, ${subgrade.label} cosmetic grade selected. ${subgrade.copy}`;
      restartFiniteAnimation();
    };

    const setGrade = (key, announce = true) => {
      const grade = GRADES[key];
      if (!grade) return;
      activeGrade = key;
      component.dataset.grade = key;
      gradeButtons.forEach((button) => {
        const selected = button.dataset.cgauGrade === key;
        button.setAttribute('aria-checked', String(selected));
        button.tabIndex = selected ? 0 : -1;
      });
      title.textContent = grade.label;
      short.textContent = grade.short;
      condition.textContent = grade.condition;
      packaging.textContent = grade.packaging;
      parcelLabel.textContent = grade.parcelLabel;
      cartonLabel.textContent = key === 'renewed' ? 'PACKAGING VARIES' : 'RETAIL CARTON';
      cartonBadgeOne.textContent = key === 'damaged' ? 'BOX MARKED' : 'FACTORY';
      cartonBadgeTwo.textContent = key === 'damaged' ? 'NEW & UNUSED' : 'SEALED';
      renewedControls.hidden = key !== 'renewed';
      if (announce) {
        status.textContent = key === 'renewed'
          ? `${grade.label}, ${SUBGRADES[activeSubgrade].label} cosmetic grade selected. ${grade.condition}`
          : `${grade.label} selected. ${grade.condition} ${grade.packaging}`;
      }
      updateIndicator();
      restartFiniteAnimation();
    };

    gradeButtons.forEach((button, index) => {
      button.addEventListener('click', () => setGrade(button.dataset.cgauGrade));
      button.addEventListener('keydown', (event) => {
        const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
        if (!keys.includes(event.key)) return;
        event.preventDefault();
        let nextIndex = index;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = gradeButtons.length - 1;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (index + 1) % gradeButtons.length;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (index - 1 + gradeButtons.length) % gradeButtons.length;
        gradeButtons[nextIndex].focus();
        gradeButtons[nextIndex].click();
      });
    });

    subgradeButtons.forEach((button, index) => {
      button.addEventListener('click', () => setSubgrade(button.dataset.cgauSubgrade));
      button.addEventListener('keydown', (event) => {
        const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
        if (!keys.includes(event.key)) return;
        event.preventDefault();
        let nextIndex = index;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = subgradeButtons.length - 1;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (index + 1) % subgradeButtons.length;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (index - 1 + subgradeButtons.length) % subgradeButtons.length;
        subgradeButtons[nextIndex].focus();
        subgradeButtons[nextIndex].click();
      });
    });

    const intersectionObserver = 'IntersectionObserver' in globalThis
      ? new IntersectionObserver((entries) => {
          inView = Boolean(entries[0]?.isIntersecting);
          component.dataset.motion = motionAllowed() ? 'ready' : 'paused';
          if (!inView) stopAnimation();
        }, { rootMargin: '100px 0px', threshold: 0.05 })
      : null;
    if (intersectionObserver) intersectionObserver.observe(component);
    else inView = true;

    const setPageVisibility = () => {
      component.dataset.motion = motionAllowed() ? 'ready' : 'paused';
      if (document.hidden) stopAnimation();
    };
    document.addEventListener('visibilitychange', setPageVisibility, { passive: true });
    const setMotionPreference = () => {
      setPageVisibility();
      if (reduceMotion?.matches) stopAnimation();
    };
    if (reduceMotion?.addEventListener) reduceMotion.addEventListener('change', setMotionPreference);
    else reduceMotion?.addListener?.(setMotionPreference);
    setPageVisibility();

    const resizeObserver = 'ResizeObserver' in globalThis ? new ResizeObserver(updateIndicator) : null;
    resizeObserver?.observe(selector);
    globalThis.addEventListener('resize', updateIndicator, { passive: true });
    globalThis.requestAnimationFrame(updateIndicator);
    setGrade('brand', false);
  };

  const init = () => document.querySelectorAll(ROOT_SELECTOR).forEach((root, index) => setup(root, index + 1));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
